import type { DocumentDto, DocumentValidationReviewDto, DocumentValueDto } from '@app/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ChevronDown, ChevronUp, Plus, Save, Trash2, Wand2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  KEY_DOCUMENT_VALUES,
  useCreateDocumentValue,
  useDeleteDocumentValue,
  useUpdateDocumentValue,
} from '../../hooks/useDocuments.js';
import { DocumentRightsButton } from '../DocumentRightsButton.js';
import { DocumentSubmitButton } from '../DocumentSubmitButton.js';
import { InlineDocumentValueValidation } from '../InlineDocumentValueValidation.js';

interface PaItem {
  clientKey: string;
  id?: string;
  numero: string;
  date: string;
  source: string;
  processus: string;
  sujet: string;
  actionEngagee: string;
  responsable: string;
  datePrevue: string;
  dateRealisee: string;
  cloture: boolean;
  accepte: boolean;
  efficacite: number;
}

interface Props {
  document: DocumentDto;
  values: DocumentValueDto[];
  readOnly?: boolean;
  changedValueIds?: Set<string>;
  validationReview?: DocumentValidationReviewDto;
}

function parsePaItem(raw: Record<string, unknown>, id?: string): PaItem {
  return {
    clientKey: id ?? crypto.randomUUID(),
    id: id,
    numero: (raw.numero as string) || '',
    date: (raw.date as string) || new Date().toISOString().slice(0, 10),
    source: (raw.source as string) || '',
    processus: (raw.processus as string) || '',
    sujet: (raw.sujet as string) || '',
    actionEngagee: (raw.actionEngagee as string) || '',
    responsable: (raw.responsable as string) || '',
    datePrevue: (raw.datePrevue as string) || '',
    dateRealisee: (raw.dateRealisee as string) || '',
    cloture: Boolean(raw.cloture),
    accepte: Boolean(raw.accepte),
    efficacite: typeof raw.efficacite === 'number' ? raw.efficacite : 0,
  };
}

function createEmptyPaItem(): PaItem {
  return {
    clientKey: crypto.randomUUID(),
    id: undefined,
    numero: '',
    date: new Date().toISOString().slice(0, 10),
    source: '',
    processus: '',
    sujet: '',
    actionEngagee: '',
    responsable: '',
    datePrevue: '',
    dateRealisee: '',
    cloture: false,
    accepte: false,
    efficacite: 0,
  };
}

/** Données d'exemple (1 ligne = 1 DocumentValue côté API). */
const DEMO_PA_ROWS: Omit<PaItem, 'clientKey' | 'id'>[] = [
  {
    numero: 'PA-2026-01',
    date: '2026-05-02',
    source: 'Audit interne',
    processus: 'Gestion documentaire',
    sujet: 'Traçabilité incomplète des enregistrements de sensibilisation sécurité',
    actionEngagee:
      'Harmoniser le modèle d’enregistrement, sensibiliser les référents site et vérifier sur un mois.',
    responsable: 'R. Qualité',
    datePrevue: '2026-06-15',
    dateRealisee: '',
    cloture: false,
    accepte: false,
    efficacite: 0,
  },
  {
    numero: 'PA-2026-02',
    date: '2026-05-05',
    source: 'Revue de direction',
    processus: 'Pilotage & amélioration',
    sujet: 'Objectifs du SMQ peu lisibles pour les équipes opérationnelles',
    actionEngagee:
      'Ateliers par site, indicateurs simplifiés et revue mensuelle des écarts avec le CODIR.',
    responsable: 'Directrice des opérations',
    datePrevue: '2026-07-30',
    dateRealisee: '',
    cloture: false,
    accepte: false,
    efficacite: 0,
  },
  {
    numero: 'PA-2026-03',
    date: '2026-04-18',
    source: 'Réclamation client',
    processus: 'Relation clients',
    sujet: 'Retard de livraison signalé sur la commande CL-4482',
    actionEngagee:
      'Analyse des causes (5M), renforcement du suivi des engagements et information proactive au client.',
    responsable: 'Chef de projet logistique',
    datePrevue: '2026-05-30',
    dateRealisee: '2026-05-28',
    cloture: true,
    accepte: true,
    efficacite: 90,
  },
];

export function ToolPaOperational({
  document,
  values,
  readOnly = false,
  changedValueIds,
  validationReview,
}: Props) {
  const queryClient = useQueryClient();
  const skipValuesSyncRef = useRef(false);
  const [rows, setRows] = useState<PaItem[]>(() =>
    values.map((v) => parsePaItem(v.serialization as Record<string, unknown>, v.id)),
  );
  const [openAccordion, setOpenAccordion] = useState<number | null>(null);
  const [hasUnsaved, setHasUnsaved] = useState(false);
  const [savingIndex, setSavingIndex] = useState<number | null>(null);
  const [fillingDemo, setFillingDemo] = useState(false);

  const createValue = useCreateDocumentValue(document.id);
  const updateValue = useUpdateDocumentValue(document.id);
  const deleteValue = useDeleteDocumentValue(document.id);

  useEffect(() => {
    if (skipValuesSyncRef.current) return;
    setRows(values.map((v) => parsePaItem(v.serialization as Record<string, unknown>, v.id)));
    setHasUnsaved(false);
  }, [values]);

  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => {
      if (hasUnsaved) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [hasUnsaved]);

  const updateRow = (index: number, partial: Partial<PaItem>) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...partial } : row)));
    setHasUnsaved(true);
  };

  const addRow = () => {
    setRows((prev) => [...prev, createEmptyPaItem()]);
    setOpenAccordion(rows.length);
    setHasUnsaved(true);
  };

  const saveRow = async (index: number) => {
    const row = rows[index];
    if (!row) return;
    setSavingIndex(index);
    const { id, clientKey, ...serialization } = row;
    void clientKey;
    try {
      if (id) {
        await updateValue.mutateAsync({ valueId: id, input: { serialization } });
      } else {
        const created = await createValue.mutateAsync({ serialization });
        setRows((prev) =>
          prev.map((r, i) => (i === index ? { ...r, id: (created as { id: string }).id } : r)),
        );
      }
      setHasUnsaved(false);
    } catch {
      alert('Erreur lors de la sauvegarde.');
    } finally {
      setSavingIndex(null);
    }
  };

  const deleteRow = async (index: number) => {
    const row = rows[index];
    if (!row) return;
    if (row.id) {
      if (!confirm('Supprimer cette action préventive ?')) return;
      try {
        await deleteValue.mutateAsync(row.id);
      } catch {
        alert('Erreur lors de la suppression.');
        return;
      }
    }
    setRows((prev) => prev.filter((_, i) => i !== index));
    if (openAccordion === index) setOpenAccordion(null);
  };

  const fillWithDemoData = async () => {
    const persistedIds = rows.filter((r) => r.id).map((r) => r.id as string);
    if (persistedIds.length > 0) {
      if (
        !confirm(
          'Les actions déjà enregistrées seront supprimées et remplacées par des exemples de démonstration. Continuer ?',
        )
      ) {
        return;
      }
    }

    skipValuesSyncRef.current = true;
    setFillingDemo(true);
    try {
      for (const id of persistedIds) {
        await deleteValue.mutateAsync(id);
      }
      const newRows: PaItem[] = [];
      for (const template of DEMO_PA_ROWS) {
        const row: PaItem = {
          clientKey: crypto.randomUUID(),
          id: undefined,
          ...template,
        };
        const { id: _id, clientKey: _ck, ...serialization } = row;
        void _id;
        void _ck;
        const created = await createValue.mutateAsync({ serialization });
        newRows.push({ ...row, id: created.id });
      }
      setRows(newRows);
      setOpenAccordion(null);
      setHasUnsaved(false);
    } catch {
      alert('Erreur lors du remplissage démo.');
      void queryClient.invalidateQueries({ queryKey: KEY_DOCUMENT_VALUES(document.id) });
    } finally {
      skipValuesSyncRef.current = false;
      setFillingDemo(false);
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-start justify-between rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div>
          <h1 className="text-lg font-bold text-slate-900">{document.name}</h1>
          <p className="mt-0.5 text-xs text-slate-500">
            {rows.length} action(s) préventive(s) / d'amélioration
          </p>
          {hasUnsaved && (
            <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
              <AlertTriangle className="h-3 w-3" />
              Modifications non sauvegardées
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!readOnly ? <DocumentSubmitButton document={document} /> : null}
          <DocumentRightsButton document={document} />
          {!readOnly ? (
            <>
              <button
                type="button"
                onClick={() => void fillWithDemoData()}
                disabled={fillingDemo}
                className="flex items-center gap-2 rounded-lg border border-violet-200 bg-violet-50 px-4 py-2 text-sm font-medium text-violet-700 transition-colors hover:bg-violet-100 disabled:opacity-50"
              >
                <Wand2 className="h-4 w-4" />
                {fillingDemo ? 'Préparation…' : 'Remplir démo'}
              </button>
              <button
                type="button"
                onClick={addRow}
                disabled={fillingDemo}
                className="flex items-center gap-2 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
              >
                <Plus className="h-4 w-4" />
                Ajouter
              </button>
            </>
          ) : null}
        </div>
      </div>

      <div className="space-y-2">
        {rows.map((row, index) => {
          const rid = row.id;
          const hl = typeof rid === 'string' && changedValueIds?.has(rid);
          const validationRow =
            validationReview?.canValidate && typeof rid === 'string'
              ? validationReview.validations.find((v) => v.documentValueId === rid)
              : undefined;
          return (
            <div
              key={row.clientKey}
              className={`rounded-xl border border-slate-200 bg-white overflow-hidden ${
                hl ? 'ring-2 ring-amber-300/90 shadow-sm' : ''
              }`}
            >
              <div className="flex items-center gap-2 px-4 py-3">
                <button
                  type="button"
                  className="flex flex-1 items-center gap-3 text-left"
                  onClick={() => setOpenAccordion(openAccordion === index ? null : index)}
                >
                  <span className="text-xs font-bold text-slate-400 w-6">{index + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="truncate text-sm font-medium text-slate-900">
                      {row.sujet || <span className="italic text-slate-400">Sans sujet</span>}
                    </p>
                    <p className="text-xs text-slate-400">
                      {row.numero ? `N° ${row.numero}` : ''}
                      {row.responsable ? ` · ${row.responsable}` : ''}
                      {row.cloture ? ' · Clôturé' : ''}
                    </p>
                  </div>
                  {openAccordion === index ? (
                    <ChevronUp className="h-4 w-4 text-slate-400" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-slate-400" />
                  )}
                </button>
                {!readOnly ? (
                  <>
                    <button
                      type="button"
                      onClick={() => saveRow(index)}
                      disabled={savingIndex === index}
                      className="flex items-center gap-1 rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
                    >
                      <Save className="h-3.5 w-3.5" />
                      {savingIndex === index ? '…' : 'Sauvegarder'}
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteRow(index)}
                      className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500"
                      title="Supprimer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </>
                ) : null}
              </div>

              {validationRow && validationReview ? (
                <InlineDocumentValueValidation
                  documentId={document.id}
                  canValidate={validationReview.canValidate}
                  validationRow={validationRow}
                />
              ) : null}

              {openAccordion === index && (
                <div
                  className={`border-t border-slate-100 px-4 pb-4 pt-3 grid grid-cols-2 gap-4 ${readOnly ? 'pointer-events-none select-none opacity-[0.96]' : ''}`}
                >
                  <label className="flex flex-col gap-1 text-xs font-medium text-slate-600">
                    N° action
                    <input
                      type="text"
                      className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-normal outline-none focus:border-emerald-500"
                      value={row.numero}
                      onChange={(e) => updateRow(index, { numero: e.target.value })}
                      placeholder="PA-001"
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-xs font-medium text-slate-600">
                    Date
                    <input
                      type="date"
                      className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-normal outline-none focus:border-emerald-500"
                      value={row.date}
                      onChange={(e) => updateRow(index, { date: e.target.value })}
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-xs font-medium text-slate-600">
                    Source
                    <input
                      type="text"
                      className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-normal outline-none focus:border-emerald-500"
                      value={row.source}
                      onChange={(e) => updateRow(index, { source: e.target.value })}
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-xs font-medium text-slate-600">
                    Processus
                    <input
                      type="text"
                      className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-normal outline-none focus:border-emerald-500"
                      value={row.processus}
                      onChange={(e) => updateRow(index, { processus: e.target.value })}
                    />
                  </label>
                  <label className="col-span-2 flex flex-col gap-1 text-xs font-medium text-slate-600">
                    Sujet *
                    <textarea
                      className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-normal outline-none focus:border-emerald-500 min-h-[60px] resize-y"
                      value={row.sujet}
                      onChange={(e) => updateRow(index, { sujet: e.target.value })}
                    />
                  </label>
                  <label className="col-span-2 flex flex-col gap-1 text-xs font-medium text-slate-600">
                    Action engagée
                    <textarea
                      className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-normal outline-none focus:border-emerald-500 min-h-[60px] resize-y"
                      value={row.actionEngagee}
                      onChange={(e) => updateRow(index, { actionEngagee: e.target.value })}
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-xs font-medium text-slate-600">
                    Responsable
                    <input
                      type="text"
                      className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-normal outline-none focus:border-emerald-500"
                      value={row.responsable}
                      onChange={(e) => updateRow(index, { responsable: e.target.value })}
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-xs font-medium text-slate-600">
                    Date prévue
                    <input
                      type="date"
                      className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-normal outline-none focus:border-emerald-500"
                      value={row.datePrevue}
                      onChange={(e) => updateRow(index, { datePrevue: e.target.value })}
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-xs font-medium text-slate-600">
                    Date réalisée
                    <input
                      type="date"
                      className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-normal outline-none focus:border-emerald-500"
                      value={row.dateRealisee}
                      onChange={(e) => updateRow(index, { dateRealisee: e.target.value })}
                    />
                  </label>
                  <label className="flex flex-col gap-1 text-xs font-medium text-slate-600">
                    Efficacité (%)
                    <input
                      type="number"
                      min={0}
                      max={100}
                      className="w-full rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-normal outline-none focus:border-emerald-500"
                      value={row.efficacite}
                      onChange={(e) => updateRow(index, { efficacite: Number(e.target.value) })}
                    />
                  </label>
                  <div className="flex items-center gap-4 pt-4">
                    <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300 text-emerald-500"
                        checked={row.cloture}
                        onChange={(e) => updateRow(index, { cloture: e.target.checked })}
                      />
                      Clôturée
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300 text-emerald-500"
                        checked={row.accepte}
                        onChange={(e) => updateRow(index, { accepte: e.target.checked })}
                      />
                      Acceptée
                    </label>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {rows.length === 0 && !readOnly && (
        <div className="mt-8 flex flex-col items-center gap-3 rounded-xl border-2 border-dashed border-slate-200 py-12 text-slate-400">
          <p className="text-sm">Aucune action préventive ou d'amélioration.</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => void fillWithDemoData()}
              disabled={fillingDemo}
              className="flex items-center gap-1.5 rounded-lg border border-violet-200 bg-violet-50 px-4 py-2 text-sm font-medium text-violet-700 hover:bg-violet-100 disabled:opacity-50"
            >
              <Wand2 className="h-4 w-4" />
              {fillingDemo ? 'Préparation…' : 'Remplir démo'}
            </button>
            <button
              type="button"
              onClick={addRow}
              disabled={fillingDemo}
              className="flex items-center gap-1.5 rounded-lg bg-emerald-500 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-50"
            >
              <Plus className="h-4 w-4" />
              Ajouter la première
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
